import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  IconButton,
  Divider,
  Paper
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { tokens } from '../../theme';
import CloseIcon from '@mui/icons-material/Close';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import GavelIcon from '@mui/icons-material/Gavel';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import InfoIcon from '@mui/icons-material/Info';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import StarsIcon from '@mui/icons-material/Stars';

const BidsInfoDialog = ({ open, onClose }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: colors.primary[400],
          backgroundImage: 'none',
          border: `1px solid ${colors.primary[300]}`
        }
      }}
    >
      <DialogTitle sx={{ 
        backgroundColor: colors.primary[500], 
        color: colors.grey[100],
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        borderBottom: `1px solid ${colors.primary[300]}`
      }}>
        <Box display="flex" alignItems="center" gap={1}>
          <GavelIcon sx={{ color: colors.blueAccent[400] }} />
          <Typography variant="h5" fontWeight="600">
            Bids System Information
          </Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ color: colors.grey[300] }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3, backgroundColor: colors.primary[400] }}>
        <Box mb={3}>
          <Typography variant="h6" color={colors.greenAccent[400]} fontWeight="600" mb={2}>
            Advanced Bid System powered by SplTrader
          </Typography>
          <Typography variant="body1" color={colors.grey[200]} mb={2}>
            Our marketplace features an intelligent bid system that automatically finds and purchases cards that match your criteria. 
            We collect 0.3% from all transactions and send it directly to the SPS burn cycle, supporting the entire Splinterlands ecosystem.
          </Typography>
        </Box>

        <Divider sx={{ backgroundColor: colors.primary[300], mb: 3 }} />

        <Box mb={3}>
          <Typography variant="h6" color={colors.blueAccent[400]} fontWeight="600" mb={2} display="flex" alignItems="center" gap={1}>
            <StarsIcon />
            Partnership & Success Rate
          </Typography>
          <Paper 
            sx={{ 
              p: 2, 
              backgroundColor: colors.primary[500],
              borderRadius: 1,
              border: `1px solid ${colors.greenAccent[400]}50`,
              mb: 2
            }}
          >
            <Box display="flex" alignItems="center" gap={2} mb={1}>
              <Chip 
                label="90%" 
                size="large"
                sx={{ 
                  backgroundColor: colors.greenAccent[400],
                  color: 'white',
                  fontWeight: 'bold',
                  fontSize: '1rem'
                }} 
              />
              <Typography variant="body1" color={colors.grey[200]} fontWeight="600">
                of our bids win against other bid services
              </Typography>
            </Box>
            <Typography variant="body2" color={colors.grey[300]}>
              Our partnership with SplTrader ensures priority processing and higher success rates compared to other platforms.
            </Typography>
          </Paper>
        </Box>

        <Box mb={3}>
          <Typography variant="h6" color={colors.redAccent[400]} fontWeight="600" mb={2} display="flex" alignItems="center" gap={1}>
            <LocalFireDepartmentIcon />
            Supporting the SPS Burn Cycle
          </Typography>
          <Box 
            p={2} 
            backgroundColor={colors.primary[500]}
            borderRadius={1}
            border={`1px solid ${colors.redAccent[400]}50`}
            mb={2}
          >
            <Typography variant="body2" color={colors.grey[200]} fontWeight="600" mb={1}>
              From every successful bid transaction:
            </Typography>
            <Box display="flex" alignItems="center" gap={1} mb={1}>
              <Chip label="0.3%" size="small" sx={{ 
                backgroundColor: colors.redAccent[400] + '20',
                color: colors.redAccent[400],
                fontWeight: 'bold'
              }} />
              <Typography variant="body2" color={colors.grey[200]}>
                goes directly to the SPS burn cycle
              </Typography>
            </Box>
            <Typography variant="body2" color={colors.grey[300]} fontStyle="italic">
              This helps strengthen the entire Splinterlands ecosystem by reducing SPS token supply.
            </Typography>
          </Box>
        </Box>

        <Box mb={3}>
          <Typography variant="h6" color={colors.greenAccent[400]} fontWeight="600" mb={2} display="flex" alignItems="center" gap={1}>
            <InfoIcon />
            How Bids Work
          </Typography>
          <List dense>
            <ListItem>
              <ListItemIcon>
                <GavelIcon sx={{ color: colors.blueAccent[400], fontSize: 20 }} />
              </ListItemIcon>
              <ListItemText 
                primary="Set Your Price & Quantity" 
                secondary="Specify the max price per BCX you're willing to pay and total BCX amount you want"
                primaryTypographyProps={{ color: colors.grey[100] }}
                secondaryTypographyProps={{ color: colors.grey[300] }}
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircleIcon sx={{ color: colors.greenAccent[400], fontSize: 20 }} />
              </ListItemIcon>
              <ListItemText 
                primary="Bid Must Be Active" 
                secondary="Only active bids participate in the marketplace. You can toggle active status anytime"
                primaryTypographyProps={{ color: colors.grey[100] }}
                secondaryTypographyProps={{ color: colors.grey[300] }}
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <AutorenewIcon sx={{ color: colors.yellow[400], fontSize: 20 }} />
              </ListItemIcon>
              <ListItemText 
                primary="Automatic BCX Reduction" 
                secondary="When a card is purchased, the 'Total BCX' decreases automatically until it reaches 0"
                primaryTypographyProps={{ color: colors.grey[100] }}
                secondaryTypographyProps={{ color: colors.grey[300] }}
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <TrendingUpIcon sx={{ color: colors.yellow[400], fontSize: 20 }} />
              </ListItemIcon>
              <ListItemText 
                primary="Max BCX per Card Limit" 
                secondary="Set the maximum BCX you want from a single card purchase to avoid overpaying for high-level cards"
                primaryTypographyProps={{ color: colors.grey[100] }}
                secondaryTypographyProps={{ color: colors.grey[300] }}
              />
            </ListItem>
          </List>
        </Box>

        <Box mb={3}>
          <Typography variant="h6" color={colors.yellow[400]} fontWeight="600" mb={2}>
            Example Scenario
          </Typography>
          <Box 
            p={2} 
            backgroundColor={colors.primary[500]}
            borderRadius={1}
            border={`1px solid ${colors.yellow[400]}50`}
          >
            <Typography variant="body2" color={colors.grey[200]} mb={1}>
              <strong>Your Bid:</strong> Price per BCX: $0.05 • Total BCX: 8 • Max BCX per Card: 3 • Status: Active
            </Typography>
            <Typography variant="body2" color={colors.grey[300]} mb={1}>
              <strong>Card Found:</strong> Level 2 card with 2 BCX at $0.04 per BCX
            </Typography>
            <Typography variant="body2" color={colors.greenAccent[400]} fontWeight="600">
              <strong>Result:</strong> Card purchased! Your Total BCX becomes 6 (8-2). Bid remains active until Total BCX reaches 0.
            </Typography>
          </Box>
        </Box>

        <Box 
          p={2} 
          backgroundColor={colors.primary[500]}
          borderRadius={1}
          border={`1px solid ${colors.primary[300]}`}
        >
          <Typography variant="body2" color={colors.grey[200]} fontStyle="italic">
            💡 <strong>Pro Tip:</strong> Keep your bids active and competitive to maximize your chances of finding great deals! 
            Our system works 24/7 to find matching cards for you.
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ 
        backgroundColor: colors.primary[500], 
        borderTop: `1px solid ${colors.primary[300]}`,
        p: 2
      }}>
        <Button 
          onClick={onClose} 
          variant="contained"
          sx={{
            backgroundColor: colors.blueAccent[600],
            color: colors.grey[100],
            '&:hover': {
              backgroundColor: colors.blueAccent[700]
            }
          }}
        >
          Got it!
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BidsInfoDialog;
